import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import {
  IconGrid, IconBroadcast,
  IconFileText, IconUsers, IconSettings, IconZap, IconLogOut, IconWifi, IconBarChart
} from './Icons';
import { C } from '../theme/space';
import api from '../lib/api';

const WA_STATUS = {
  ready:         { color: '#34d399', label: 'Connected' },
  authenticated: { color: '#fbbf24', label: 'Authenticating' },
  qr_ready:      { color: '#fb923c', label: 'Scan QR' },
  disconnected:  { color: '#f87171', label: 'Offline' },
};

const FEATURE_ROUTES = {
  dashboard:  { to: '/',                      label: 'Dashboard',          Icon: IconGrid,      end: true },
  whatsapp:   { to: '/whatsapp',              label: 'WhatsApp & Bot',     Icon: IconZap              },
  broadcast:  { to: '/broadcast',             label: 'Broadcaster',        Icon: IconBroadcast        },
  analytics:  { to: '/broadcast/analytics',   label: 'Analytics',          Icon: IconBarChart         },
  templates:  { to: '/templates',             label: 'Templates',          Icon: IconFileText         },
  leads:      { to: '/leads',                 label: 'Leads',              Icon: IconUsers            },
  users:      { to: '/users',                 label: 'Users & Licenses',   Icon: IconSettings         },
};

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { waStatus } = useSocket();
  const [showPwModal, setShowPwModal] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);

  const waCfg     = WA_STATUS[waStatus] || WA_STATUS.disconnected;
  const isOrgAdmin = user?.role === 'orgadmin';
  const isSuperAdmin = user?.role === 'superadmin';
  const features  = user?.allowedFeatures || ['dashboard'];
  const links     = ['dashboard', 'whatsapp', 'broadcast', 'analytics', 'templates', 'leads', ...(isOrgAdmin || isSuperAdmin ? ['users'] : [])]
    .filter(f => isSuperAdmin || features.includes(f) || f === 'dashboard')
    .map(f => FEATURE_ROUTES[f])
    .filter(Boolean);

  async function handleChangePassword(e) {
    e.preventDefault();
    setPwError('');
    if (pwForm.next !== pwForm.confirm) { setPwError('Passwords do not match'); return; }
    if (pwForm.next.length < 8) { setPwError('Minimum 8 characters'); return; }
    setPwLoading(true);
    try {
      await api.post('/api/auth/change-password', { currentPassword: pwForm.current, newPassword: pwForm.next });
      setPwSuccess(true);
      setTimeout(() => { setShowPwModal(false); setPwSuccess(false); setPwForm({ current: '', next: '', confirm: '' }); logout(); }, 2000);
    } catch (err) {
      setPwError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setPwLoading(false);
    }
  }

  return (
    <>
      <motion.aside
        style={s.sidebar}
        initial={{ x: -20, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
      >
        {/* Logo */}
        <div style={s.logo}>
          <div style={s.logoMark}><IconZap size={15} color="#fff" /></div>
          <div>
            <div style={s.logoText}>Broadcaster</div>
            {isOrgAdmin && <div style={s.roleTag}>Org Admin</div>}
          </div>
        </div>

        {/* WhatsApp Status */}
        <div style={s.waStatus}>
          <IconWifi size={12} color={waCfg.color} />
          <span style={{ ...s.waLabel, color: waCfg.color }}>{waCfg.label}</span>
          <span style={s.waText}>WhatsApp</span>
        </div>

        {/* Nav Links */}
        <nav style={s.nav}>
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              style={({ isActive }) => ({ ...s.link, ...(isActive ? s.linkActive : {}) })}
            >
              {({ isActive }) => (
                <>
                  <l.Icon size={15} color={isActive ? C.primary : C.textMuted} />
                  <span>{l.label}</span>
                  {isActive && <div style={s.activePip} />}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Footer */}
        <div style={s.footer}>
          <div style={s.userRow}>
            <div style={s.avatar}>{(user?.name || user?.email || '?')[0].toUpperCase()}</div>
            <div style={s.userInfo}>
              <span style={s.userName}>{user?.name || user?.email}</span>
              {user?.name && <span style={s.userEmail}>{user.email}</span>}
            </div>
          </div>
          <button style={s.pwBtn} onClick={() => { setShowPwModal(true); setPwError(''); setPwSuccess(false); }}>
            🔑 Change Password
          </button>
          <button style={s.logoutBtn} onClick={logout}>
            <IconLogOut size={13} color={C.textMuted} />
            <span>Sign Out</span>
          </button>
        </div>
      </motion.aside>

      {/* Change Password Modal */}
      {showPwModal && (
        <div style={s.overlay} onClick={() => setShowPwModal(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <h3 style={s.modalTitle}>Change Password</h3>
            {pwSuccess ? (
              <p style={{ color: '#34d399', textAlign: 'center', fontSize: 14 }}>Password changed! Signing you out…</p>
            ) : (
              <form onSubmit={handleChangePassword} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {['current', 'next', 'confirm'].map((field, i) => (
                  <input
                    key={field}
                    type="password"
                    placeholder={['Current password', 'New password', 'Confirm new password'][i]}
                    value={pwForm[field]}
                    onChange={e => setPwForm(f => ({ ...f, [field]: e.target.value }))}
                    required
                    style={s.modalInput}
                  />
                ))}
                {pwError && <p style={s.modalError}>{pwError}</p>}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" style={s.cancelBtn} onClick={() => setShowPwModal(false)}>Cancel</button>
                  <button type="submit" style={s.submitBtn} disabled={pwLoading}>
                    {pwLoading ? 'Saving…' : 'Update Password'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}

const s = {
  sidebar: { width: 228, minHeight: '100vh', background: 'rgba(3, 8, 22, 0.95)', borderRight: '1px solid rgba(99,102,241,0.12)', display: 'flex', flexDirection: 'column', padding: '20px 12px', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', flexShrink: 0, position: 'relative', zIndex: 10 },
  logo: { display: 'flex', alignItems: 'center', gap: 10, padding: '0 6px 18px', borderBottom: '1px solid rgba(99,102,241,0.1)', marginBottom: 6 },
  logoMark: { width: 30, height: 30, borderRadius: 8, background: 'linear-gradient(135deg, #6366f1, #818cf8)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 3px 12px rgba(99,102,241,0.35)', flexShrink: 0 },
  logoText: { fontWeight: 700, fontSize: 15, color: C.text, letterSpacing: -0.3 },
  roleTag: { fontSize: 10, color: C.success, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', marginTop: 1 },
  waStatus: { display: 'flex', alignItems: 'center', gap: 6, padding: '10px 8px', marginBottom: 6 },
  waLabel: { fontSize: 12, fontWeight: 600 },
  waText: { fontSize: 11, color: C.textMuted, marginLeft: 2 },
  nav: { display: 'flex', flexDirection: 'column', gap: 2, flex: 1 },
  link: { display: 'flex', alignItems: 'center', gap: 9, padding: '9px 10px', borderRadius: 8, fontSize: 13, color: C.textMuted, textDecoration: 'none', position: 'relative', transition: 'color 0.15s, background 0.15s', fontWeight: 500 },
  linkActive: { background: 'rgba(99,102,241,0.12)', color: C.text, fontWeight: 600 },
  activePip: { position: 'absolute', right: 10, width: 5, height: 5, borderRadius: '50%', background: C.primary, boxShadow: `0 0 8px ${C.primary}` },
  footer: { borderTop: '1px solid rgba(99,102,241,0.1)', paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 8 },
  userRow: { display: 'flex', alignItems: 'center', gap: 9, padding: '0 4px' },
  avatar: { width: 30, height: 30, borderRadius: 8, background: 'rgba(99,102,241,0.2)', border: '1px solid rgba(99,102,241,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, color: C.primary, flexShrink: 0 },
  userInfo: { display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  userName: { fontSize: 12, fontWeight: 600, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  userEmail: { fontSize: 10, color: C.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  pwBtn: { display: 'flex', alignItems: 'center', gap: 6, width: '100%', padding: '7px 10px', background: 'transparent', border: '1px solid rgba(99,102,241,0.15)', borderRadius: 8, color: C.textMuted, cursor: 'pointer', fontSize: 12, fontWeight: 500 },
  logoutBtn: { display: 'flex', alignItems: 'center', gap: 7, width: '100%', padding: '8px 10px', background: 'transparent', border: '1px solid rgba(99,102,241,0.1)', borderRadius: 8, color: C.textMuted, cursor: 'pointer', fontSize: 12, fontWeight: 500 },
  // Modal
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#0d1424', border: '1px solid rgba(99,102,241,0.25)', borderRadius: 16, padding: '28px 32px', width: 360, boxShadow: '0 0 60px rgba(0,0,0,0.6)' },
  modalTitle: { color: C.text, fontSize: 17, fontWeight: 700, marginBottom: 20 },
  modalInput: { padding: '11px 13px', borderRadius: 8, background: 'rgba(2,5,16,0.8)', border: '1px solid rgba(99,102,241,0.2)', color: C.text, fontSize: 14, outline: 'none', width: '100%' },
  modalError: { color: '#f87171', fontSize: 13, padding: '8px 12px', background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)', borderRadius: 8 },
  cancelBtn: { flex: 1, padding: '10px', background: 'transparent', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 8, color: C.textMuted, cursor: 'pointer', fontSize: 14 },
  submitBtn: { flex: 2, padding: '10px', background: 'linear-gradient(135deg,#6366f1,#818cf8)', border: 'none', borderRadius: 8, color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 14 },
};
